/**
 * `CallSession` → `VoiceControl`. The only file that knows both.
 *
 * Everything interesting here is the mapping, and two parts of it are decisions
 * rather than translation.
 *
 * ---------------------------------------------------------------------------
 * ONE: `thinking` IS DERIVED, BECAUSE THE PROVIDER NEVER SAYS IT.
 *
 * The wire carries `speaking: { user, assistant }`, both driven by real events —
 * local VAD for the user, `output_audio_buffer.*` for the assistant. There is no
 * event for "the user has stopped and the agent has not started", which is the
 * single most important stretch of a voice interaction to show, because it is the
 * one where a person is waiting and needs to know something is happening. Without
 * it every widget looks identical to `listening` while the model thinks, and a
 * half-second of that reads as a dropped call.
 *
 * So it is derived: user was speaking, user stopped, assistant has not started yet.
 * A short grace window keeps it from firing in the natural gaps INSIDE speech —
 * every stop consonant is a VAD gap of tens of milliseconds, and flickering
 * `listening → thinking → listening` on the word "packet" would be worse than not
 * having the state at all.
 *
 * ---------------------------------------------------------------------------
 * TWO: THE ERROR TEXT IS REWRITTEN FOR AN END USER.
 *
 * `CallSnapshot.error` is written for whoever is debugging the platform — it says
 * things like "the session could not be created: 503 api_unconfigured". That is
 * exactly right on the diagnostics page and exactly wrong in a widget on a dental
 * practice's website, where the reader can do nothing about it and the sentence
 * only teaches them to distrust the page. So the messages a widget shows are a
 * small closed set, and the original goes to the console for whoever installed it.
 *
 * The one error a visitor CAN act on is a denied microphone, which is why that case
 * keeps its own specific wording.
 */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { CallSession } from '@/lib/call'
import type { CallBackend } from '@/lib/call/backend'
import type { CallSnapshot } from '@/lib/call'
import type { VoiceCard, VoiceControl, VoicePhase, VoiceToolCall, VoiceTranscriptLine } from './types'

/**
 * How long the user must be silent before it counts as `thinking`.
 *
 * 220 ms. Longer than the gap inside a word (a stop consonant is 20–80 ms, a comma
 * pause 100–200 ms) and shorter than a person notices as lag. Below ~150 ms the
 * state flickers mid-sentence; above ~400 ms the model has usually started
 * replying already and the state never shows at all, which is the same as not
 * having it.
 */
const THINKING_AFTER_MS = 220

/** How long `thinking` may persist before it stops being reassuring and starts
 *  looking stuck. After this the widget falls back to `listening`, which is honest:
 *  the microphone IS still open and the turn may still be the user's. */
const THINKING_MAX_MS = 12_000

export interface UseVoiceControlOptions {
  agentId: string
  /** Defaults to the panel's authenticated API. The embed passes its own. */
  backend?: CallBackend
  /** Written onto the session row, so a playground call is distinguishable from a
   *  real one when reading the sessions list later. */
  label?: string
  /** Called with the raw snapshot error, for whoever installed the widget. */
  onError?: (detail: string) => void
}

export function useVoiceControl(opts: UseVoiceControlOptions): VoiceControl {
  // Created once and never recreated. A call outlives renders; see the header of
  // lib/call/index.ts for why this is an object and not a hook.
  const [session] = useState(() => new CallSession())
  const snap = useSyncExternalStore(session.subscribe, session.getSnapshot)

  /**
   * The audio element the agent's voice plays through.
   *
   * IT MUST BE IN THE DOCUMENT — see transport.ts. A detached element is not
   * guaranteed to play on any engine and does not on Safari, which presents as a
   * connected call with no sound. Created here rather than rendered as JSX so that
   * a widget author cannot forget it, and hidden rather than `display:none`
   * because some engines decline to play a display-none element.
   */
  const audioRef = useRef<HTMLAudioElement | null>(null)
  useEffect(() => {
    const el = document.createElement('audio')
    el.autoplay = true
    // Not `hidden`, not `display:none`. Off-screen and zero-opacity keeps it a
    // real, rendered element that every engine will play.
    el.setAttribute('aria-hidden', 'true')
    el.style.cssText = 'position:absolute;width:0;height:0;opacity:0;pointer-events:none'
    document.body.appendChild(el)
    audioRef.current = el
    return () => {
      el.remove()
      audioRef.current = null
    }
  }, [])

  // End the call if the component goes away. A widget being unmounted while live
  // would otherwise leave the microphone open and the session billing.
  useEffect(() => () => void session.end(), [session])

  // --- the derived `thinking` window ---------------------------------------
  const [thinking, setThinking] = useState(false)
  const userWasSpeaking = useRef(false)
  useEffect(() => {
    const { user, assistant } = snap.speaking
    if (assistant) {
      // The agent is talking. Never `thinking`.
      setThinking(false)
      userWasSpeaking.current = false
      return
    }
    if (user) {
      setThinking(false)
      userWasSpeaking.current = true
      return
    }
    // Silence, and the user was mid-turn. Wait out the grace window before
    // calling it thinking, so a pause inside a sentence does not flicker.
    if (!userWasSpeaking.current) return
    const enter = setTimeout(() => setThinking(true), THINKING_AFTER_MS)
    const giveUp = setTimeout(() => {
      setThinking(false)
      userWasSpeaking.current = false
    }, THINKING_MAX_MS)
    return () => {
      clearTimeout(enter)
      clearTimeout(giveUp)
    }
  }, [snap.speaking])

  const phase = mapPhase(snap, thinking)

  // --- the error, rewritten -------------------------------------------------
  const onError = opts.onError
  const rawError = snap.error
  useEffect(() => {
    if (rawError) onError?.(rawError)
  }, [rawError, onError])

  const transcript = useMemo<VoiceTranscriptLine[]>(
    () => snap.transcript.map((l) => ({ id: l.itemId, who: l.who, text: l.text, final: l.final })),
    [snap.transcript],
  )

  /**
   * `ToolActivity` narrowed to what a widget can paint.
   *
   * The two epoch stamps collapse into one duration here rather than in the widget,
   * so a component that draws a chip never has to know which clock they are on —
   * they are the browser's, and they are display furniture (see the header of
   * `components/call/transcript.tsx`). `endedAtMs` is null while the call is still
   * running, and `Math.max(0, …)` guards the one case that produces a negative: a
   * clock that stepped backwards mid-call.
   */
  const tools = useMemo<VoiceToolCall[]>(
    () =>
      snap.tools.map((t) => ({
        id: t.callId,
        name: t.name,
        displayName: t.displayName,
        displayDescription: t.displayDescription,
        state: t.state,
        seconds: t.endedAtMs === null ? null : Math.max(0, (t.endedAtMs - t.startedAtMs) / 1000),
        anchorIndex: t.anchorIndex,
      })),
    [snap.tools],
  )

  /** `CardActivity` narrowed the same way: `atMs` is display furniture the
   *  widgets never show, so it is dropped rather than carried unused. */
  const cards = useMemo<VoiceCard[]>(
    () =>
      snap.cards.map((c) => ({
        id: c.callId,
        title: c.title,
        body: c.body,
        anchorIndex: c.anchorIndex,
      })),
    [snap.cards],
  )

  /**
   * The teardown currently in flight, or null.
   *
   * THIS EXISTS BECAUSE THE HANG-UP IS OPTIMISTIC. Widgets no longer render the
   * `ending` stretch (see `VoicePhase`), so the button says "start" again while
   * the old call is still draining — and `CallSession.start` guards against
   * `live` and `starting` but not against a teardown, so an eager second click
   * used to interleave the two: the new call would build a peer connection, a
   * recorder and a meter, and the tail of the old `end()` would then null them
   * and set the phase to `ended` underneath it.
   *
   * Held here rather than fixed in `CallSession` deliberately. The transport does
   * not have this problem on its own — it arose from a decision about what a
   * button shows — and this is the layer that owns what a button's presses mean.
   */
  const drainingRef = useRef<Promise<void> | null>(null)

  const start = useCallback(() => {
    const el = audioRef.current
    if (!el) return
    const go = () =>
      void session.start({
        agentId: opts.agentId,
        audioEl: el,
        ...(opts.label === undefined ? {} : { label: opts.label }),
        ...(opts.backend === undefined ? {} : { backend: opts.backend }),
      })
    // Queued behind the drain rather than refused: refusing would be a click that
    // does nothing on a button that looks ready, which is the worse failure.
    const draining = drainingRef.current
    if (draining) void draining.then(go)
    else go()
  }, [session, opts.agentId, opts.label, opts.backend])

  const stop = useCallback(() => {
    // A SECOND STOP KEEPS THE FIRST HANDLE. `CallSession.end` returns early when a
    // teardown is already running, so its promise resolves immediately rather than
    // when the drain finishes — storing that would say "nothing to wait for" while
    // there very much is. No widget can produce the second click (the button reads
    // "start" the moment the first lands), but the playground calls `stop` too.
    if (drainingRef.current) {
      void session.end()
      return
    }
    const done = session.end()
    drainingRef.current = done
    void done.finally(() => {
      if (drainingRef.current === done) drainingRef.current = null
    })
  }, [session])
  const toggleMute = useCallback(() => session.setMuted(!session.getSnapshot().muted), [session])
  const sendText = useCallback((text: string) => session.sendText(text), [session])
  // Stable across renders, and NOT a value: read it in an animation frame.
  const levels = useCallback((): { user: number; assistant: number } => session.levels(), [session])
  const spectrum = useCallback(
    (track: 'user' | 'assistant', out: Uint8Array): number => session.spectrum(track, out),
    [session],
  )

  return useMemo<VoiceControl>(
    () => ({
      phase,
      stage: snap.startStage,
      takenOver: snap.takenOver,
      error: phase === 'error' ? friendlyError(snap) : null,
      notices: snap.notices,
      muted: snap.muted,
      startedAtMs: snap.startedAtMs,
      transcript,
      tools,
      cards,
      levels,
      spectrum,
      start,
      stop,
      toggleMute,
      sendText,
    }),
    [phase, snap, transcript, tools, cards, levels, spectrum, start, stop, toggleMute, sendText],
  )
}

function mapPhase(snap: CallSnapshot, thinking: boolean): VoicePhase {
  switch (snap.phase) {
    case 'idle':
      return 'idle'
    // Both are "wait a moment" to a user. The distinction between probing support
    // and creating the session only matters to whoever is debugging it.
    case 'checking':
    case 'starting':
      return 'connecting'
    case 'blocked':
    case 'failed':
      return 'error'
    // BOTH ARE `ended`, which is the optimistic hang-up: the teardown is still
    // running and the widget says the call is over, because it is — see the
    // note on `VoicePhase` in types.ts for why a state of its own was a flicker
    // rather than progress.
    case 'ending':
    case 'ended':
      return 'ended'
    case 'live':
      // A person holds the call: there is no model to be thinking, and no
      // speech events to say when they talk. The status line says who it is.
      if (snap.takenOver) return 'listening'
      if (snap.speaking.assistant) return 'speaking'
      if (thinking) return 'thinking'
      return 'listening'
  }
}

/**
 * The closed set of sentences a visitor may see.
 *
 * Deliberately short and deliberately not derived from the underlying message.
 * A visitor on a customer's website can act on exactly one of these — the
 * microphone one — and everything else is our problem, so it says so in one line
 * and stops. The real message is already on its way to `onError`.
 */
function friendlyError(snap: CallSnapshot): string {
  const raw = snap.error ?? ''
  // THE SUPPORT REFUSAL IS DECIDED FIRST, before any wording is inspected. It
  // used to come after the microphone match below, and the iOS refusal's own text
  // contained the word "microphone" — so an iPhone visitor read "No microphone
  // was found", a hardware fault they did not have, instead of being told the
  // page could not do this here (seen on gene.4js.ai, 2026-09-04). A blocked
  // phase is never a device problem, whatever its sentence happens to say.
  if (snap.phase === 'blocked') {
    return 'This browser cannot make voice calls here. Open the page in Chrome, Edge or Safari.'
  }
  if (/microphone access was denied|NotAllowedError|SecurityError/i.test(raw)) {
    return 'Microphone access is blocked. Allow it in your browser’s address bar, then try again.'
  }
  if (/microphone/i.test(raw)) {
    return 'No microphone was found. Connect one and try again.'
  }
  return 'The call could not be connected. Please try again in a moment.'
}
